import axios from "axios";
import { API_URL } from "../constants";
import {
  CreateDeliveryInput,
  UpdateDeliveryInput,
  DeliveryItemInput,
  FetchDeliveriesResponse,
  DeliveryResponse,
  DeliveryItemResponse,
  DeleteResponse,
} from "types/Delivery";

// ============ DELIVERIES ============

// Fetch all deliveries for a company
export const fetchDeliveries = async (
  companyId: string,
  filters?: { status?: string; orderId?: string; customerId?: string }
): Promise<FetchDeliveriesResponse> => {
  let url = `${API_URL}/${companyId}/deliveries`;
  if (filters) {
    const params = new URLSearchParams();
    if (filters.status) params.append("status", filters.status);
    if (filters.orderId) params.append("orderId", filters.orderId);
    if (filters.customerId) params.append("customerId", filters.customerId);
    const queryString = params.toString();
    if (queryString) url += `?${queryString}`;
  }
  const response = await axios.get(url);
  return response.data;
};

// Fetch deliveries for a specific order
export const fetchDeliveriesByOrder = async (
  companyId: string,
  orderId: string
): Promise<FetchDeliveriesResponse> => {
  const url = `${API_URL}/${companyId}/orders/${orderId}/deliveries`;
  const response = await axios.get(url);
  return response.data;
};

// Fetch single delivery by ID
export const fetchDeliveryById = async (
  companyId: string,
  deliveryId: string
): Promise<DeliveryResponse> => {
  const url = `${API_URL}/${companyId}/deliveries/${deliveryId}`;
  const response = await axios.get(url);
  return response.data;
};

// Create a new delivery
export const createDelivery = async (
  companyId: string,
  orderId: string,
  data: CreateDeliveryInput
): Promise<DeliveryResponse> => {
  const url = `${API_URL}/${companyId}/orders/${orderId}/deliveries`;
  const response = await axios.post(url, data);
  return response.data;
};

// Update a delivery
export const updateDelivery = async (
  companyId: string,
  deliveryId: string,
  data: UpdateDeliveryInput
): Promise<DeliveryResponse> => {
  const url = `${API_URL}/${companyId}/deliveries/${deliveryId}`;
  const response = await axios.patch(url, data);
  return response.data;
};

// Delete a delivery (draft only)
export const deleteDelivery = async (
  companyId: string,
  deliveryId: string
): Promise<DeleteResponse> => {
  const url = `${API_URL}/${companyId}/deliveries/${deliveryId}`;
  const response = await axios.delete(url);
  return response.data;
};

// ============ DELIVERY ITEMS ============

// Add item to delivery
export const addDeliveryItem = async (
  companyId: string,
  deliveryId: string,
  data: DeliveryItemInput
): Promise<DeliveryItemResponse> => {
  const url = `${API_URL}/${companyId}/deliveries/${deliveryId}/items`;
  const response = await axios.post(url, data);
  return response.data;
};

// Update delivery item
export const updateDeliveryItem = async (
  companyId: string,
  deliveryId: string,
  itemId: string,
  data: { quantity?: number; notes?: string }
): Promise<DeliveryItemResponse> => {
  const url = `${API_URL}/${companyId}/deliveries/${deliveryId}/items/${itemId}`;
  const response = await axios.patch(url, data);
  return response.data;
};

// Delete delivery item
export const deleteDeliveryItem = async (
  companyId: string,
  deliveryId: string,
  itemId: string
): Promise<DeleteResponse> => {
  const url = `${API_URL}/${companyId}/deliveries/${deliveryId}/items/${itemId}`;
  const response = await axios.delete(url);
  return response.data;
};

// ============ AVAILABLE RESERVATIONS ============

export interface AvailableReservationItem {
  id: string;
  reservationId: string;
  reservationNumber: number;
  product: {
    id: string;
    title?: string;
    sku?: string;
    supplier_sku?: string;
  };
  warehouse: {
    id: string;
    name?: string;
  } | null;
  quantity: number;
  fulfilledQuantity: number;
  availableQuantity: number;
  orderLineType?: string;
  orderLineId?: string;
}

export interface AvailableReservationsResponse {
  availableItems: AvailableReservationItem[];
}

// Fetch available reservation items for delivery
export const fetchAvailableReservations = async (
  companyId: string,
  orderId: string
): Promise<AvailableReservationsResponse> => {
  const url = `${API_URL}/${companyId}/orders/${orderId}/reservations/available`;
  const response = await axios.get(url);
  return response.data;
};
